import { MesaLogo } from '@/src/assets/icons/MesaLogo'
import { MesaMonogram } from '@/src/assets/icons/MesaMonogram'
import { MesaHeader } from '@/src/common/modules/RewardsSection/MesaHeader'
import { RewardOptionEntryPoint } from '@/src/common/modules/RewardsSection/RewardOptionEntryPoint'
import { useRewardOptions } from '@/src/common/modules/RewardsSection/useRewardOptions'
import { MainWrapper } from '@/src/common/wrappers/MainWrapper'
import { View, Text } from 'react-native'
import { useMediaQuery } from 'react-responsive'
import { ReferAFriendHeader } from '@/src/app/main/refer-a-friend'
import { ChevronWithIcon } from '@/src/common/components/ChevronWithIcon'
import { router } from 'expo-router'
import { safeLineHeight } from '@/src/common/utils/safeLineHeight'
import { MesaFooter } from '@/src/common/components/MesaFooter'
import { DesignSystem } from '@/src/design-system'
import { fontFamilies, mesaColorNames, typography } from '@/src/common/theme'

function useGap() {
  return useMediaQuery({ minWidth: 720 }) ? 32 : 24
}

function RewardsOptions() {
  const rewardOptions = useRewardOptions()
  const minWidth1411 = useMediaQuery({ minWidth: 1411 })
  const minWidth1150 = useMediaQuery({ minWidth: 1150 })
  const minWidth1024 = useMediaQuery({ minWidth: 1024 })
  const minWidth831 = useMediaQuery({ minWidth: 831 })
  const minWidth588 = useMediaQuery({ minWidth: 588 })
  const minWidth320 = useMediaQuery({ minWidth: 320 })
  const minWidth238 = useMediaQuery({ minWidth: 238 })
  const numColumns = minWidth1411
    ? 5
    : minWidth1150
      ? 4
      : minWidth1024
        ? 4
        : minWidth831
          ? 5
          : minWidth588
            ? 4
            : minWidth320
              ? 3
              : minWidth238
                ? 2
                : 1

  const cellsPresentInLastRow = rewardOptions.length % numColumns
  const cellsAbsentInLastRow = cellsPresentInLastRow ? numColumns - cellsPresentInLastRow : 0
  const paddedRewardOptions = [
    ...rewardOptions,
    ...Array.from({ length: cellsAbsentInLastRow }).map(() => undefined),
  ] as const

  return paddedRewardOptions.map((rewardOption, index) => (
    <View
      key={rewardOption?.title || index}
      style={{ flexGrow: 1, flexBasis: `${100 / (numColumns + 1)}%` }}
    >
      {rewardOption && <RewardOptionEntryPoint rewardOption={rewardOption} />}
    </View>
  ))
}

export default function RewardsLegacyScreen() {
  const gap = useGap()

  const minWidth1024 = useMediaQuery({ minWidth: 1024 })

  return (
    <MainWrapper
      screen="/main/rewards"
      bodyProps={{
        style: {
          flexDirection: minWidth1024 ? 'row' : 'column',
          flexWrap: 'nowrap',
          gap,
          alignItems: 'flex-start',
        },
      }}
      footer={<MesaFooter style={{ width: '100%' }} />}
    >
      <DesignSystem.Card
        style={[
          { padding: 0 },
          minWidth1024 ? { flexGrow: 2, flexShrink: 3, flexBasis: '40%' } : { width: '100%' },
        ]}
      >
        <MesaHeader>
          <MesaMonogram />
          <View style={{ position: 'absolute', margin: 'auto', alignItems: 'center' }}>
            <MesaLogo variant="dualtone-white" height={53} width={139} />
            <Text
              style={{
                fontFamily: fontFamilies['PlusJakartaSans-ExtraBold'],
                fontWeight: 800,
                fontSize: 42,
                letterSpacing: 0,
                ...safeLineHeight(37.8),
                color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
                textAlign: 'center',
              }}
            >
              Rewards
            </Text>
          </View>
        </MesaHeader>
        <View style={{ margin: 16, gap: 8, flexGrow: 1 }}>
          <Text
            style={[
              typography['heading/heading3'],
              { color: mesaColorNames['Mesa - Brand/Secondary #223843'] },
            ]}
          >
            Rewards, Benefits, & Offers
          </Text>
          <Text style={[typography.S, { color: mesaColorNames['Greyscale/900 #152438'] }]}>
            Redeem Mesa Points at partner brands, towards your next vacation, or as statement
            credits with the Mesa Homeowners Card.
          </Text>
        </View>
      </DesignSystem.Card>

      <View
        style={[
          { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap },
          minWidth1024 ? { flexGrow: 3, flexShrink: 2, flexBasis: '60%' } : { width: '100%' },
        ]}
      >
        <View
          style={{
            flexBasis: '100%',
            backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
            borderRadius: 16,
            padding: 32,
            display: 'flex',
            flexDirection: minWidth1024 ? 'row' : 'column',
            flexWrap: 'nowrap',
            gap,
          }}
        >
          <ReferAFriendHeader
            textContainerProps={{
              style: { alignItems: 'flex-start', flexShrink: 1 },
              children: (
                <ChevronWithIcon
                  text="Send referrals"
                  segmentAction='Pressed "Send referrals" button'
                  segmentComponent="Reward Option"
                  containerStyle={{
                    backgroundColor: 'transparent',
                    paddingLeft: 0,
                    paddingBottom: 0,
                    gap: 8,
                    alignSelf: 'flex-start',
                  }}
                  textProps={{
                    style: {
                      color: mesaColorNames['Greyscale/0 #FFFFFF'],
                      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
                      fontWeight: 700,
                      fontSize: 14,
                      lineHeight: 21,
                      letterSpacing: 0,
                    },
                  }}
                  onPress={() => router.push('/main/refer-a-friend')}
                  chevronRightProps={{ stroke: mesaColorNames['Greyscale/0 #FFFFFF'] }}
                />
              ),
            }}
            headerTextStyle={{ textAlign: 'left' }}
            subheaderTextStyle={{ textAlign: 'left' }}
          />
        </View>

        <RewardsOptions />
      </View>
    </MainWrapper>
  )
}
